export const getCampaignRawStatus = (statusOrCampaign, isPaused = false) => {
    const isCampaignObject =
        statusOrCampaign &&
        typeof statusOrCampaign === "object" &&
        !Array.isArray(statusOrCampaign);
    const status = isCampaignObject
        ? statusOrCampaign.status
        : statusOrCampaign;
    const normalizedStatus =
        typeof status === "string" ? status.trim().toLowerCase() : "";

    if (normalizedStatus) return normalizedStatus;

    const fallbackPaused =
        isPaused === true ||
        statusOrCampaign?.ui_helper_context?.is_paused === true;

    return fallbackPaused ? "paused" : "";
};

export const normalizeCampaignStatus = (statusOrCampaign, isPaused = false) => {
    const normalizedStatus = getCampaignRawStatus(statusOrCampaign, isPaused);

    if (
        normalizedStatus === "paused_infrastructure_impediment" ||
        normalizedStatus.startsWith("paused")
    ) {
        return "paused";
    }

    if (
        normalizedStatus === "preparing" ||
        normalizedStatus === "pending_research"
    ) {
        return "preparing";
    }

    if (normalizedStatus.includes("failed")) {
        return "failed";
    }

    if (
        normalizedStatus === "competed" ||
        normalizedStatus.includes("completed") ||
        normalizedStatus.includes("complete")
    ) {
        return "completed";
    }

    if (normalizedStatus.includes("processing")) {
        return "processing";
    }

    if (
        normalizedStatus.includes("active") ||
        normalizedStatus.includes("running")
    ) {
        return "active";
    }

    return normalizedStatus || "unknown";
};

export const isCampaignCapHolding = (campaign) =>
    [
        "active",
        "preparing",
        "pending_research",
        "paused",
        "paused_infrastructure_impediment",
    ].includes(getCampaignRawStatus(campaign));

export const getCampaignActionState = (
    statusOrCampaign,
    isPaused = false,
) => {
    const rawStatus = getCampaignRawStatus(statusOrCampaign, isPaused);

    const canEditContext = ["active", "paused"].includes(rawStatus);
    const canPause = rawStatus === "active";
    const canSetActive = rawStatus === "paused";
    const needsSenderReauth =
        rawStatus === "paused_infrastructure_impediment";

    return {
        rawStatus,
        canEditContext,
        canPause,
        canSetActive,
        needsSenderReauth,
        isReadOnly: !canEditContext && !canPause && !canSetActive,
    };
};

export const getCampaignStatusPresentation = (
    statusOrCampaign,
    isPaused = false,
) => {
    const rawStatus = getCampaignRawStatus(statusOrCampaign, isPaused);
    const normalizedStatus = normalizeCampaignStatus(statusOrCampaign, isPaused);

    if (rawStatus === "paused_infrastructure_impediment") {
        return {
            normalizedStatus,
            label: "Infra Paused",
            styleKey: "infraPaused",
        };
    }

    if (normalizedStatus === "paused") {
        return { normalizedStatus, label: "Paused", styleKey: "paused" };
    }

    if (normalizedStatus === "preparing") {
        return {
            normalizedStatus,
            label: "Preparing",
            styleKey: "preparing",
        };
    }

    if (normalizedStatus === "processing") {
        return {
            normalizedStatus,
            label: "Processing",
            styleKey: "processing",
        };
    }

    if (normalizedStatus === "active" || normalizedStatus === "running") {
        return { normalizedStatus, label: "Active", styleKey: "active" };
    }

    if (normalizedStatus === "completed") {
        const failedCount = Number(statusOrCampaign?.metrics?.failed);
        const hasFailures = Number.isFinite(failedCount) && failedCount > 0;

        if (hasFailures) {
            return {
                normalizedStatus,
                label: "Completed with failures",
                styleKey: "completedWithFailures",
            };
        }

        return {
            normalizedStatus,
            label: "Completed",
            styleKey: "completed",
        };
    }

    if (normalizedStatus === "failed") {
        return { normalizedStatus, label: "Failed", styleKey: "failed" };
    }

    const fallbackLabel =
        normalizedStatus === "unknown"
            ? "Unknown"
            : normalizedStatus
                  .replace(/[_-]+/g, " ")
                  .replace(/\s+/g, " ")
                  .trim()
                  .replace(/^./, (character) => character.toUpperCase());

    return {
        normalizedStatus,
        label: fallbackLabel,
        styleKey: "unknown",
    };
};

export const formatCampaignDate = (value) => {
    if (!value) return "Unavailable";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
};

export const clampPercentage = (value) => {
    const percentage = Number(value);

    if (!Number.isFinite(percentage)) return 0;

    return Math.min(100, Math.max(0, percentage));
};

export const getCampaignMessageMetrics = (metrics = {}) => {
    const getCount = (value) => {
        const count = Number(value);

        return Number.isFinite(count) && count > 0 ? Math.trunc(count) : 0;
    };
    const pending = getCount(metrics.pending);
    const processing = getCount(metrics.processing);
    const sent = getCount(metrics.sent);
    const skipped = getCount(metrics.skipped);
    const failed = getCount(metrics.failed);
    const totalMessages = pending + processing + sent + skipped + failed;
    const processedMessages = sent + skipped + failed;
    const executionProgress =
        totalMessages > 0
            ? clampPercentage((processedMessages / totalMessages) * 100)
            : 0;
    const getPercentage = (count) =>
        totalMessages > 0
            ? clampPercentage((count / totalMessages) * 100)
            : 0;

    return {
        pending,
        processing,
        sent,
        skipped,
        failed,
        totalMessages,
        processedMessages,
        executionProgress,
        processingPercentage: getPercentage(processing),
        sentPercentage: getPercentage(sent),
        skippedPercentage: getPercentage(skipped),
        failedPercentage: getPercentage(failed),
    };
};

const getPositiveWholeNumber = (value) => {
    const number = Number(value);

    return Number.isFinite(number) && number > 0 ? Math.trunc(number) : 0;
};

export const getCampaignProgressState = (
    campaign = {},
    fallbackTotalSteps = 0,
) => {
    const helperContext = campaign?.ui_helper_context || {};
    const messageMetrics = getCampaignMessageMetrics(campaign?.metrics);
    const progressPercentage =
        messageMetrics.totalMessages > 0
            ? messageMetrics.executionProgress
            : clampPercentage(helperContext.progress_percentage);
    const segmentCount = getPositiveWholeNumber(
        campaign?.total_steps ?? fallbackTotalSteps,
    );
    const currentActiveStep = getPositiveWholeNumber(
        helperContext.current_active_step,
    );
    const completedSteps =
        normalizeCampaignStatus(campaign) === "completed"
            ? segmentCount
            : currentActiveStep
              ? Math.min(Math.max(currentActiveStep - 1, 0), segmentCount)
              : 0;

    return {
        progressPercentage,
        segmentCount,
        hasSegmentedProgress: segmentCount > 0,
        completedSteps,
        currentActiveStep,
    };
};

const sentenceCase = (value) => {
    const normalized = value.trim().toLowerCase();

    if (!normalized) return "";

    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

export const formatMachineText = (value) => {
    if (!value) return "Unavailable";

    const normalized = String(value)
        .trim()
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ");

    if (!normalized) return "Unavailable";

    const words = normalized.split(" ");
    const startsWithCampaignPaused =
        words[0]?.toLowerCase() === "campaign" &&
        words[1]?.toLowerCase() === "paused";

    if (startsWithCampaignPaused && words.length > 2) {
        return "Campaign paused";
    }

    return sentenceCase(normalized);
};
